import { OpenEditWindowParams, ReactAction, ReactActionId } from "@multiweb/types/react";
import { IAction } from "UI/UI.Controls/Actions/IAction";
import { IControl } from "UI/UI/UI/Controls/ControlInterfaces";
import { IDisposable } from "../../Global/Base/IDisposable";
import { IMultiselectComponent } from "../UI/UI/XGControls/Frames/FrameInterfaces";
import { IFrame } from "../UI/UI/XGControls/IFrame";

export type AnyFunction<A = any> = (...input: any[]) => A;
export type AnyConstructor<A = object> = new (...input: any[]) => A;

export type Mixin<T extends AnyFunction> = InstanceType<ReturnType<T>>;

/**
 * Rozhraní pro konektor propojující aplikaci a frame.
 */
export interface IReactFrameConnector extends IFrame, IDisposable {
    /** Název detail formu z definice formuláře. */
    readonly detailFormName: string;
    /** Nainicializované akce */
    readonly objectActions: ReadonlyArray<IAction>;
    /** Značí, zdali je umožněno dočítání dalších dat. */
    loadOnScroll: boolean;
    /** Inicializuje celý frame. Musí být zavoláno před jeho prvním použitím. Je možné spouštět asynchronní procesy. */
    initializeVisual(): Promise<void>;
    /** Inicializuje akce v toolbaru */
    initializeActions(): Promise<void>;
    /**
     * Nastaví viditelnost refresh akce.
     * @param isVisible Hodnota, zda je akce viditelná.
     */
    setRefreshActionVisibility(isVisible: boolean): void;
    /**
     * Nastaví viditelnost jedné akce z toolbaru.
     * Viditelnost akcí pro režim hromadného výběru se nastavuje v mixinu {@link HasMultiselect},
     * v metodě {@link HasMultiSelect.setToolbarActionEnabledVisual}.
     *
     * @param action Akce z toolbaru.
     * @param value Hodnota, zda je akce viditelná.
     */
    setToolbarActionEnabledVisual(action: IAction, value: boolean): void;
    /** Vytvoří ze seznamu akcí typu IAction nový seznam akcí typu ReactActions a vrátí jej. */
    getReactRowActions(): ReactAction[];
    /**
     * Zavolání execute metody na dané akci.
     *
     * @param actionId Identifikátor akce, která se má provést.
     * @param ids Identifikátory záznamů, pro které se má akce provést.
     * @param className Třída, na které se má akce provést.
     */
    executeAction(actionId: ReactActionId, ids: ReactActionId[], className: string): void;
    /** Zobrazení dialogu s informacemi o záznamu. Nic neprovede, pokud na frame není určen 'detailFormName'. */
    openDetailForm(dataObjectId: string): Promise<void>;
}

export class IReactFrameConnector {
    private static _properties: (keyof IReactFrameConnector)[] = ["detailFormName", "objectActions", "loadOnScroll"];
    private static _methods: (keyof IReactFrameConnector)[] = ["getReactRowActions", "executeAction", "openDetailForm"];
    private static _interfaces = [IFrame, IDisposable];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            this._properties.every((value) => Reflect.has(instance, value)) &&
            this._methods.every((value) => typeof instance[value] === "function") &&
            this._interfaces.every((_interface) => instance instanceof _interface);
    }
}

/**
 * Rozhraní pro GridLayout konektor, slouží k vyrenderování elementů i když ještě nejsou ve viditelné části aplikace.
 */
export interface IPostInitialize {
    /** Funkce, která se zavolá po inicializaci vnitřních komponent. */
    postInitialize(): void;
}

export class IPostInitialize {
    private static _methods: (keyof IPostInitialize)[] = ["postInitialize"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            this._methods.every((value) => typeof instance[value] === "function");
    }
}

/**
 * Rozhraní pro LiteListFrame konektor propojující aplikaci a frame.
 */
export interface ILiteListFrameConnector extends IReactFrameConnector {
    /** Funkce, která otevře editačního okno daného atributu v záznamu. */
    openAttributeEditWindow(params: OpenEditWindowParams): Promise<void>;
}

export class ILiteListFrameConnector {
    private static _methods: (keyof ILiteListFrameConnector)[] = ["openAttributeEditWindow"];
    private static _interfaces = [IReactFrameConnector];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            this._methods.every((value) => typeof instance[value] === "function") &&
            this._interfaces.every((_interface) => instance instanceof _interface);
    }
}

/**
 * Rozhraní frame konektoru s režimem výběru více záznamů.
 */
export interface IReactFrameConnectorWithMultiselect extends IReactFrameConnector, IHasMultiselect
{}

export class IReactFrameConnectorWithMultiselect {
    public static [Symbol.hasInstance](instance: any): boolean {
        return (
            instance != null && instance instanceof IReactFrameConnector && instance instanceof IHasMultiselect
        );
    }
}

export interface IGeneralReactFrameConnector extends IControl {
    renderComponent(rootElement: HTMLElement, onForm: boolean): void;
}

export class IGeneralReactFrameConnector {
    private static _methods: (keyof IGeneralReactFrameConnector)[] = ["renderComponent"];
    private static _interfaces = [IControl];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            this._methods.every((value) => typeof instance[value] === "function") &&
            this._interfaces.every((_interface) => instance instanceof _interface);
    }
}

/**
 * Rozhraní mixinu pro režim výběru více záznamů.
 */
export interface IHasMultiselect extends IMultiselectComponent {
    /** Určuje, zda je Frame v režimu výběru více záznamů. */
    multiselectMode: boolean;
    /** Příznak, zda je označen alespoň jeden záznam. */
    atLeastOneRowChecked: boolean;
    /** Určuje, zda je na Frame povolen režim hromadného výběru. */
    allowMultiSelect: boolean;
    /**
     * Nastavení MultiselectModu pro connector.
     * Při změně MultiselectMode se aktualizuje viditelnost akcí (objectActions, multiObjectActions, ale také akcí v toolbaru).
     *
     * @param multiselectMode Nová hodnota MultiselectMódu.
     * @param dispatchActions určuje, zda se má změna promítnout do store. (Pokud se volá z **reactí části, musí být false**, aby nevznikl infinite loop).
     */
    setMultiselectMode(multiselectMode: boolean, dispatchActions: boolean): void;
    /** Přidá toolbar akce pro operace nad více záznamy. */
    addMultiSelectActions(): void;
}

export class IHasMultiselect {
    private static _properties: (keyof IHasMultiselect)[] = [
        "multiselectMode",
        "atLeastOneRowChecked",
    ];
    private static _methods: (keyof IHasMultiselect)[] = [
        "setMultiselectMode",
        "addMultiSelectActions",
    ];
    private static _interfaces = [IMultiselectComponent];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null
            && this._properties.every((value) => Reflect.has(instance, value))
            && this._methods.every((method) => typeof instance[method] === "function")
            && this._interfaces.every((_interface) => instance instanceof _interface);
    }
}
